import { ReadonlyVec2 } from 'gl-matrix';
import { enqueueSnackbar } from '~/utils/notifications';
import { TargetCrosshair } from '~/engine/data/common/customs/cursors/TargetCrosshair';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { ModifierKeys } from '~/engine/tool/ModifierKeys';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { View } from '~/engine/view/View';
import { ViewManager } from '~/engine/view/ViewManager';
import { PlaneView } from '../../../view/views/PlaneView';
import { LineDrawActionState } from '../../actions/contouring-actions/LineDrawAction';
import { MOUSE_BUTTON_LEFT, MOUSE_BUTTON_RIGHT } from '../../MouseButton';
import { CrosshairInteractionMode, Tool } from '../../Tool';

export class LineDrawTool extends Tool {
    public readonly toolType = 'line';
    private _cursorAction?: TargetCrosshair;
    private readonly image: ImageObject;
    private readonly structure: RegionOfInterest;
    private readonly isErasing: boolean;
    protected readonly _planeView: PlaneView;
    protected _drawState?: LineDrawActionState;

    /** Whether the left mouse button is currently held down.
     *  Used to distinguish click-to-set-points (mouse up) from
     *  free-form drawing (mouse held + drag). */
    private _isMouseButtonHeld = false;

    public override isActionInProgress(): boolean {
        return this._drawState?.drawing === true;
    }

    public override getDrawingView(): PlaneView | undefined {
        return this.isActionInProgress() ? this._planeView : undefined;
    }

    public override isStrokeInProgress(): boolean {
        return this.isActionInProgress() && this._isMouseButtonHeld;
    }

    public override getCrosshairInteractionMode(
        isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.contouringCrosshairMode(
            isDrawingActiveInOtherView,
            this.isStrokeInProgress()
        );
    }

    public override usesButtonDuringAction(button: number): boolean {
        return button === MOUSE_BUTTON_RIGHT && this.isActionInProgress();
    }

    public constructor(
        image: ImageObject,
        structure: RegionOfInterest,
        mode: 'draw' | 'erase',
        view: PlaneView
    ) {
        super();
        this.image = image;
        this.structure = structure;
        this.isErasing = mode === 'erase';
        this._planeView = view;
        this.initializeCursor();
    }

    private initializeCursor() {
        if (this.structure.canModify) {
            this._cursorAction = new TargetCrosshair(
                this._planeView,
                this.isErasing,
                true
            );
        }
    }

    private isDrawingView(view: PlaneView): boolean {
        return view === this._planeView;
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: View
    ): EventHandlerResult {
        if (this._cursorAction) {
            this._cursorAction.setCustomCursor();
        } else {
            this.initializeCursor();
        }

        if (this._drawState) {
            return this._drawState.onMouseMove(coordinates, buttons, view);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this.structure.canModify === false) {
            this.resetDrawAction(view);
            enqueueSnackbar(
                'Modifying approved structures is not allowed. Unapprove the structure to make changes.',
                { variant: 'warning' }
            );
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (button === MOUSE_BUTTON_LEFT && this.isDrawingView(view)) {
            this._isMouseButtonHeld = true;
            view.snapToImagePixel();
            if (!this._drawState) {
                this.resetDrawAction(view);
            }

            if (this._drawState) {
                this._drawState.mouseHeld = true;
                this._drawState.onMousePress(modifierKeys, coordinates, view);
            }
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }

        // Block other actions during free-form drawing, and
        // block panning in side viewports during point-by-point
        // drawing — same policy as scroll blocking.
        if (
            this.isStrokeInProgress() ||
            (this.isActionInProgress() && !this.isDrawingView(view))
        ) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2,
        view?: View
    ): EventHandlerResult {
        if (button === MOUSE_BUTTON_LEFT) {
            this._isMouseButtonHeld = false;
            if (this._drawState) {
                this._drawState.mouseHeld = false;
                const result = this._drawState.onMouseRelease(
                    coordinates,
                    view
                );
                ViewManager.viewUpdateEvent(
                    new VolumeViewUpdateEvent(
                        this.structure.boundingBox,
                        LayerBit.CONTOUR_LAYER_BIT,
                        true
                    )
                );
                return result;
            }
        }
        if (
            button === MOUSE_BUTTON_RIGHT &&
            this.isActionInProgress() &&
            !this.isStrokeInProgress()
        ) {
            if (!this._drawState?.undoLastPoint()) {
                this._drawState?.discardDrawing();
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_HANDLED,
                    true,
                    new VolumeViewUpdateEvent(
                        this.structure.boundingBox,
                        LayerBit.CONTOUR_LAYER_BIT,
                        true
                    )
                );
            }
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseWheel(
        _delta: 1 | -1,
        _modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseDoubleClick(
        coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        if (this._drawState) {
            return this._drawState.finishOnPoint(coordinates);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view: View
    ): EventHandlerResult {
        if (key === 'Escape' && this.isActionInProgress()) {
            this._drawState?.discardDrawing();
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true,
                new VolumeViewUpdateEvent(
                    this.structure.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    protected resetDrawAction(view: View) {
        if (view instanceof PlaneView) {
            this._drawState?.dispose();
            this._drawState = new LineDrawActionState(
                view,
                this.structure,
                this.image,
                this.isErasing
            );
        }
    }

    public override refreshCursor(): void {
        this._cursorAction?.setCustomCursor();
    }

    public onDeactivate(): EventHandlerResult {
        this._isMouseButtonHeld = false;
        let result: EventHandlerResult | undefined;
        if (this._drawState) {
            result = this._drawState.stopAction();
            this._drawState.dispose();
            this._drawState = undefined;
        }
        this._cursorAction?.stopAction();
        this._cursorAction?.resetCursor();
        this._cursorAction = undefined;

        return (
            result ??
            new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED)
        );
    }
}
